import React, { useState } from "react";

// COMMOM Import
import PrimaryButton from "../../CommonButtons/PrimaryButton";

// UTLIS Import
import { linkIdentifier } from "../../../../utils/linkIdentifier";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider";

// Suggested prop reference for consumers -------------------------------------
/**
 * @typedef {((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void)} OnClickEventHandler
 */
/**
 * @typedef {("button" | "submit")} BtnType
 */
/**
 * @typedef {("white" | "black")} HoverOption
 */
/**
 * @typedef {Object} ChipItem
 * @property {string} text - Chip label.
 * @property {string} icon - Chip icon.
 * @property {boolean} selected - Highlight chip and render bullet list when true.
 * @property {boolean} [disabled] - Disable click/hover behaviour.
 * @property {HoverOption} [hoverOption] - Hover theme for enabled chips.
 * @property {string[]} [bulletPoint] - Optional bullet items shown under the chip when selected.
 * @property {(e: React.MouseEvent<HTMLDivElement>, index: number, chip: ChipItem) => void} [onClick] - Chip click handler.
 */
/**
 * @param {Object} props
 * @param {string} props.headerTitle - Main heading text displayed at the top.
 * @param {string} [props.headerFontWeight] - Heading font weight class (e.g. "normal", "semibold", "bold").
 * @param {string} [props.headerFontColor] - Heading text color class (e.g. "text-primary").
 * @param {string} [props.headerFontSize] - Heading text size class (e.g. "font-md").
 * @param {string} [props.subtext] - Supporting description text shown under the headers.
 * @param {ChipItem[]} props.chipsArray - Interactive options rendered as chips.
 * @param {string} props.btnOneText - Primary (left) button label.
 * @param {BtnType} [props.btnOneBtnType] - Primary button type attribute.
 * @param {OnClickEventHandler} props.btnOneHandleFunction - Primary button click handler.
 * @param {boolean} [props.btnOneIsDisabled] - Disable primary button.
 * @param {string} [props.btnOneEnabledTextColor] - Primary button text color when enabled.
 * @param {string} [props.btnOneDisabledTextColor] - Primary button text color when disabled.
 * @param {string} [props.btnOneEnabledBgColor] - Primary button background when enabled.
 * @param {string} [props.btnOneDisabledBgColor] - Primary button background when disabled.
 * @param {string} [props.btnOneOutLineColor] - Primary button outline/border color.
 * @param {boolean} [props.btnOneFullWidthRequired] - Stretch primary button to full width.
 * @param {HoverOption} [props.btnOneHoverOption] - Primary button hover theme.
 * @param {boolean} [props.btnOneIsLoading] - Show loading state on primary button.
 * @param {React.RefObject<HTMLButtonElement>} [props.btnOneButtonRef] - Ref for the primary button.
 * @param {boolean} [props.btnOneInActiveVariantNeeded] - Render primary button inactive variant.
 * @param {boolean} [props.btnOneIsLoaderNeeded] - Render loader indicator inside primary button.
 * @param {string} props.btnTwoText - Secondary (right) button label.
 * @param {BtnType} [props.btnTwoBtnType] - Secondary button type attribute.
 * @param {OnClickEventHandler} props.btnTwoHandleFunction - Secondary button click handler.
 * @param {boolean} [props.btnTwoIsDisabled] - Disable secondary button.
 * @param {string} [props.btnTwoEnabledTextColor] - Secondary button text color when enabled.
 * @param {string} [props.btnTwoDisabledTextColor] - Secondary button text color when disabled.
 * @param {string} [props.btnTwoEnabledBgColor] - Secondary button background when enabled.
 * @param {string} [props.btnTwoDisabledBgColor] - Secondary button background when disabled.
 * @param {string} [props.btnTwoOutLineColor] - Secondary button outline/border color.
 * @param {boolean} [props.btnTwoFullWidthRequired] - Stretch secondary button to full width.
 * @param {HoverOption} [props.btnTwoHoverOption] - Secondary button hover theme.
 * @param {boolean} [props.btnTwoIsLoading] - Show loading state on secondary button.
 * @param {React.RefObject<HTMLButtonElement>} [props.btnTwoButtonRef] - Ref for the secondary button.
 * @param {boolean} [props.btnTwoInActiveVariantNeeded] - Render secondary button inactive variant.
 * @param {boolean} [props.btnTwoIsLoaderNeeded] - Render loader indicator inside secondary button.
 * @returns {JSX.Element}
 */
const SwitchCommonPopup = ({
  headerTitle,
  headerFontWeight,
  headerFontColor,
  headerFontSize,
  btnOneBtnType,
  btnOneDisabledTextColor,
  btnOneDisabledBgColor,
  btnOneIsLoading,
  btnOneButtonRef,
  btnOneInActiveVariantNeeded,
  btnOneIsLoaderNeeded,
  btnOneHandleFunction,
  btnOneEnabledBgColor,
  btnOneOutLineColor,
  btnOneEnabledTextColor,
  btnOneIsDisabled,
  btnOneHoverOption,
  btnOneFullWidthRequired,
  btnTwoText,
  btnTwoBtnType,
  btnTwoDisabledTextColor,
  btnTwoIsLoading,
  btnTwoButtonRef,
  btnTwoInActiveVariantNeeded,
  btnTwoIsLoaderNeeded,
  btnTwoHandleFunction,
  btnTwoEnabledBgColor,
  btnTwoDisabledBgColor,
  btnTwoOutLineColor,
  btnTwoEnabledTextColor,
  btnTwoIsDisabled,
  btnTwoFullWidthRequired,
  btnTwoHoverOption,
  chipsArray,
}) => {
  // useState
  const [animate, setAnimate] = useState(false);

  const safeBtnOneHandler =
    typeof btnOneHandleFunction === "function"
      ? btnOneHandleFunction
      : () => {};
  const safeBtnTwoHandler =
    typeof btnTwoHandleFunction === "function"
      ? btnTwoHandleFunction
      : () => {};
  const safeChipsArray = Array.isArray(chipsArray) ? chipsArray : [];

  const toSentenceCase = (text = "") =>
    text.charAt(GlobalConfiguration?.lengthCheckZero).toUpperCase() +
    text.slice(GlobalConfiguration?.lengthCheckOne).toLowerCase();

  return (
    <div
      className="flex w-full justify-center items-center m-auto top-1/2 left-1/2 fixed z-z-index-modal p-padding-sm"
      style={{ transform: "translate(-50%, -50%)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={` max-h-small-popup-height w-full Qmd:w-small-popup-width flex flex-col  py-padding-md bg-primary-bg rounded-border-radius-md animate-none ${animate ? "Qmd:animate-slideInFromLeft" : "Qmd:animate-slideInFromRight"}`}
      >
        <div className="h-full flex flex-col overflow-hidden">
          <p
            className={`text-${headerFontColor} text-${headerFontSize} font-${headerFontWeight} px-padding-md`}
          >
            {toSentenceCase(headerTitle)}
          </p>

          <div
            className={`mt-margin-md flex flex-col gap-gap-sm overflow-y-auto flex-1 px-padding-md h-max-height-top-slider`}
          >
            {safeChipsArray.map((item, index) => (
              <div
                className="flex flex-row h-topnavheight"
                key={index}
                onClick={(e) => {
                  if (!item.disabled && item?.onClick) {
                    item?.onClick(e, index, item);
                  }
                }}
              >
                <div className="p-padding-sm border-r flex items-center  bg-text-primary rounded-l-border-radius-md">
                  <img
                    src={item.icon}
                    className="w-icon-lg h-icon-lg"
                    alt="Logo"
                  />
                </div>
                <p
                  className={`${item.selected ? "bg-positive-bg text-text-primary" : "text-text-primary bg-primary-nav "} ${item.disabled ? "opacity-light-opacity cursor-not-allowed " : ""} ${item.disabled ? "" : item.selected ? "" : item.hoverOption ? `global-hover-${item.hoverOption} cursor-pointer` : ""} flex items-center pl-padding-sm relative z-0 rounded-r-border-radius-md text-font-md w-width-full`}
                >
                  {item.text}
                </p>
              </div>
            ))}
          </div>
          <div
            className={`text-text-secondary text-font-xs font-normal px-padding-md ${
              !safeChipsArray.some(
                (item) =>
                  item.selected &&
                  Array.isArray(item.bulletPoint) &&
                  item.bulletPoint.length >
                    GlobalConfiguration?.lengthCheckZero,
              ) && "hidden"
            } ${
              safeChipsArray.some(
                (item) =>
                  item.selected &&
                  Array.isArray(item.bulletPoint) &&
                  item.bulletPoint.length >
                    GlobalConfiguration?.lengthCheckZero,
              ) && "pt-padding-md"
            }`}
          >
            {safeChipsArray.map(
              (item, index) =>
                item.selected &&
                Array.isArray(item.bulletPoint) &&
                item.bulletPoint.length >
                  GlobalConfiguration.lengthCheckZero && (
                  <ul
                    key={index}
                    className="list-disc pl-padding-md flex flex-col gap-gap-sm"
                  >
                    {item.bulletPoint.map((bulletObj, objIndex) => {
                      const sectionKey = Object.keys(bulletObj)[0];
                      const sectionValues = bulletObj[sectionKey];

                      return (
                        <li
                          key={objIndex}
                          className="text-text-secondary text-font-sm font-normal"
                        >
                          {sectionKey}
                          {Array.isArray(sectionValues) &&
                            sectionValues.length >
                              GlobalConfiguration?.lengthCheckZero &&
                            "-"}

                          {Array.isArray(sectionValues) &&
                            sectionValues.length >
                              GlobalConfiguration?.lengthCheckZero && (
                              <ul className="mt-gap-xs flex flex-col gap-gap-xs list-none">
                                {sectionValues.map((value, valIndex) => (
                                  <li
                                    key={valIndex}
                                    className="text-text-secondary text-font-sm font-normal flex items-center gap-gap-xs"
                                  >
                                    <span>-</span>
                                    <span>{linkIdentifier(value)}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                        </li>
                      );
                    })}
                  </ul>
                ),
            )}
          </div>
          <div className="w-full gap-gap-sm px-padding-md flex justify-end pt-padding-md">
            <PrimaryButton
              btnText={"Cancel"}
              btnType={btnOneBtnType}
              handleFunction={() => {
                if (!btnOneIsDisabled) {
                  setAnimate(true);
                  setTimeout(() => {
                    safeBtnOneHandler();
                  }, IntervalConfiguration.intervalSemiMid);
                }
              }}
              isDisabled={btnOneIsDisabled}
              enabledTextColor={btnOneEnabledTextColor}
              disabledTextColor={btnOneDisabledTextColor}
              enabledBgColor={btnOneEnabledBgColor}
              disabledBgColor={btnOneDisabledBgColor}
              outLineColor={btnOneOutLineColor}
              fullWidthRequired={btnOneFullWidthRequired}
              hoverOption={btnOneHoverOption}
              isLoading={btnOneIsLoading}
              buttonRef={btnOneButtonRef}
              inActiveVariantNeeded={btnOneInActiveVariantNeeded}
              isLoaderNeeded={btnOneIsLoaderNeeded}
            />
            <PrimaryButton
              btnText={btnTwoText}
              handleFunction={() => {
                if (!btnTwoIsDisabled) {
                  setAnimate(true);
                  setTimeout(() => {
                    safeBtnTwoHandler();
                  }, IntervalConfiguration.intervalSemiMid);
                }
              }}
              enabledBgColor={btnTwoEnabledBgColor}
              outLineColor={btnTwoOutLineColor}
              enabledTextColor={btnTwoEnabledTextColor}
              isDisabled={btnTwoIsDisabled}
              fullWidthRequired={btnTwoFullWidthRequired}
              hoverOption={btnTwoHoverOption}
              btnType={btnTwoBtnType}
              disabledTextColor={btnTwoDisabledTextColor}
              disabledBgColor={btnTwoDisabledBgColor}
              isLoading={btnTwoIsLoading}
              buttonRef={btnTwoButtonRef}
              inActiveVariantNeeded={btnTwoInActiveVariantNeeded}
              isLoaderNeeded={btnTwoIsLoaderNeeded}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwitchCommonPopup;
